/* Shared Torque header, ticker, and desktop/mobile navigation behavior. */
(() => {
  'use strict';

  const ROOT_SELECTOR = '.torque-homepage-v6';

  function init(root) {
    if (!root || root.dataset.torqueChromeInitialized === 'true') return;

    const header = root.querySelector('header.site');
    if (!header) return;
    root.dataset.torqueChromeInitialized = 'true';

    const ticker = root.querySelector('.ticker');
    const compactNavQuery = window.matchMedia('(max-width: 720px)');
    const menuToggle = root.querySelector('.menu-toggle');
    const mobileMenu = root.querySelector('.mobile-menu');
    const mobileMenuClose = root.querySelector('.mobile-menu-close');
    const mobileMenuBackdrop = root.querySelector('.mobile-menu-backdrop');
    const openLabel = menuToggle?.getAttribute('aria-label');
    const closeLabel = mobileMenuClose?.getAttribute('aria-label');
    const cleanups = [];
    const observers = [];
    const searchToggle = header.querySelector('[data-search-toggle]');
    const searchPanel = header.querySelector('.header-search');
    function closeSearch(restoreFocus = false) {
      if (!searchPanel || searchPanel.hidden) return;
      searchPanel.hidden = true;
      searchToggle?.setAttribute('aria-expanded', 'false');
      if (restoreFocus) searchToggle?.focus({ preventScroll: true });
    }
    const cartBadge = header.querySelector('#cartn');
    if (cartBadge) {
      const syncCartBadge = () => { cartBadge.hidden = Number(cartBadge.textContent.trim()) <= 0; };
      const cartBadgeObserver = new MutationObserver(syncCartBadge);
      cartBadgeObserver.observe(cartBadge, { childList: true, characterData: true, subtree: true });
      observers.push(cartBadgeObserver);
      syncCartBadge();
    }
    let mobileMenuCloseTimer = 0;
    let mobileMenuReturnFocus = null;
    let tickerNaturalHeight = 0;
    let tickerFrame = 0;

    const listen = (target, type, handler, options) => {
      if (!target) return;
      target.addEventListener(type, handler, options);
      cleanups.push(() => target.removeEventListener(type, handler, options));
    };

    // Native details keep every destination usable when JavaScript is disabled.
    if (searchToggle && searchPanel) {
      const searchInput = searchPanel.querySelector('input');
      const searchForm = searchPanel.querySelector('form');
      const results = searchPanel.querySelector('.header-search__results');
      const status = searchPanel.querySelector('.header-search__status');
      const destinations = searchPanel.querySelector('.header-search__destinations');
      let searchTimer;
      let searchRequest;
      let searchVersion = 0;
      async function updateSearch() {
        window.clearTimeout(searchTimer);
        searchRequest?.abort();
        const version = ++searchVersion;
        const query = searchInput.value.trim();
        results.replaceChildren();
        results.hidden = true;
        destinations.hidden = Boolean(query);
        status.textContent = query ? 'Searching…' : '';
        if (!query) return;
        searchRequest = new AbortController();
        const url = new URL(searchPanel.dataset.searchUrl, window.location.origin);
        url.search = new URLSearchParams({q: query, 'resources[type]': 'product,page,article', 'resources[limit]': '6', 'resources[limit_scope]': 'each', section_id: 'torque-search-results'}).toString();
        try {
          const response = await fetch(url, {signal: searchRequest.signal});
          if (!response.ok) throw new Error('Search unavailable');
          const html = new DOMParser().parseFromString(await response.text(), 'text/html');
          if (version !== searchVersion) return;
          const content = html.querySelector('[data-search-result-count]');
          if (!content) throw new Error('Search unavailable');
          const count = Number(content.dataset.searchResultCount);
          results.replaceChildren(content);
          results.hidden = !count;
          status.textContent = count ? `Matches for “${query}”` : `No matches for “${query}”. Try another search.`;
        } catch (error) {
          if (version !== searchVersion || error.name === 'AbortError') return;
          status.textContent = 'Search is unavailable right now. Try again or explore below.';
          destinations.hidden = false;
        }
      }
      listen(searchInput, 'input', () => {
        window.clearTimeout(searchTimer);
        searchRequest?.abort();
        ++searchVersion;
        results.replaceChildren();
        results.hidden = true;
        status.textContent = searchInput.value.trim() ? 'Searching…' : '';
        destinations.hidden = Boolean(searchInput.value.trim());
        searchTimer = window.setTimeout(updateSearch, 200);
      });
      listen(searchForm, 'submit', event => { event.preventDefault(); updateSearch(); });
      cleanups.push(() => { window.clearTimeout(searchTimer); ++searchVersion; searchRequest?.abort(); });
      const incomingQuery = new URLSearchParams(window.location.search).get('search');
      if (incomingQuery !== null) {
        searchInput.value = incomingQuery;
        // Run after the rest of the shared navigation has initialized.
        window.setTimeout(() => { if (root.isConnected) { searchToggle.click(); updateSearch(); } }, 0);
        const cleanUrl = new URL(window.location.href);
        cleanUrl.searchParams.delete('search');
        window.history.replaceState(window.history.state, '', cleanUrl);
      }
      searchToggle.setAttribute('role', 'button');
      listen(searchToggle, 'click', event => {
        if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        if (!searchPanel.hidden) return closeSearch(true);
        closeDesktopMenus();
        setMobileMenu(false, { restoreFocus: false });
        searchPanel.hidden = false;
        searchToggle.setAttribute('aria-expanded', 'true');
        searchPanel.querySelector('input')?.focus({ preventScroll: true });
      });
      listen(searchToggle, 'keydown', event => {
        if (event.key === ' ') { event.preventDefault(); searchToggle.click(); }
      });
      listen(searchPanel.querySelector('.header-search__close'), 'click', () => closeSearch(true));
      listen(document, 'keydown', event => {
        if (event.key === 'Escape' && !searchPanel.hidden) {
          event.preventDefault();
          closeSearch(true);
        }
      });
      listen(document, 'pointerdown', event => {
        if (!searchPanel.contains(event.target) && !searchToggle.contains(event.target)) {
          closeSearch(searchPanel.contains(document.activeElement));
        }
      });
      listen(document, 'focusin', event => {
        if (!searchPanel.contains(event.target) && !searchToggle.contains(event.target)) closeSearch();
      });
    }
    // Enhancement adds pointer intent, one open panel, and keyboard dismissal.
    const desktopMenus = [...header.querySelectorAll('.desktop-menu')];
    const hoverQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
    let desktopOpenTimer = 0;
    let desktopCloseTimer = 0;
    let pointerOpenedMenu = null;

    function clearDesktopTimers() {
      window.clearTimeout(desktopOpenTimer);
      window.clearTimeout(desktopCloseTimer);
    }

    function closeDesktopMenus({ restoreFocus = false } = {}) {
      clearDesktopTimers();
      pointerOpenedMenu = null;
      desktopMenus.forEach(menu => {
        if (menu.open && restoreFocus && menu.contains(document.activeElement)) {
          menu.querySelector('summary')?.focus({ preventScroll: true });
        }
        menu.open = false;
      });
    }

    function openDesktopMenu(menu, { pointer = false } = {}) {
      if (compactNavQuery.matches) return;
      closeSearch();
      clearDesktopTimers();
      pointerOpenedMenu = pointer ? menu : null;
      desktopMenus.forEach(other => { other.open = other === menu; });
    }

    desktopMenus.forEach(menu => {
      const trigger = menu.querySelector('summary');
      listen(menu, 'toggle', () => {
        if (menu.open) {
          closeSearch();
          desktopMenus.forEach(other => { if (other !== menu) other.open = false; });
        }
      });
      listen(menu, 'pointerenter', event => {
        if (!hoverQuery.matches || event.pointerType !== 'mouse' || compactNavQuery.matches) return;
        clearDesktopTimers();
        desktopOpenTimer = window.setTimeout(() => openDesktopMenu(menu, { pointer: true }), 120);
      });
      listen(menu, 'pointerleave', event => {
        if (event.pointerType !== 'mouse') return;
        clearDesktopTimers();
        if (!menu.contains(document.activeElement)) {
          desktopCloseTimer = window.setTimeout(() => { menu.open = false; }, 200);
        }
      });
      listen(trigger, 'click', event => {
        clearDesktopTimers();
        // A first pointer click confirms a hover-opened panel; a second closes it.
        if (menu.open && pointerOpenedMenu === menu && event.detail > 0) event.preventDefault();
        pointerOpenedMenu = null;
        // The summary's native click supplies mouse, touch, Enter and Space.
        desktopMenus.forEach(other => { if (other !== menu) other.open = false; });
      });
      listen(trigger, 'keydown', event => {
        if (compactNavQuery.matches || event.key !== 'ArrowDown') return;
        event.preventDefault();
        openDesktopMenu(menu);
        menu.querySelector('.desktop-panel a[href]')?.focus({ preventScroll: true });
      });
      listen(menu, 'click', event => {
        if (event.target.closest('a[href]')) closeDesktopMenus();
      });
    });
    listen(document, 'keydown', event => {
      if (event.key !== 'Escape' || !desktopMenus.some(menu => menu.open)) return;
      event.preventDefault();
      closeDesktopMenus({ restoreFocus: true });
    });
    listen(document, 'pointerdown', event => {
      if (!desktopMenus.some(menu => menu.contains(event.target))) closeDesktopMenus({ restoreFocus: true });
    });
    listen(document, 'focusin', event => {
      desktopMenus.forEach(menu => {
        if (!menu.contains(event.target)) menu.open = false;
      });
    });
    listen(compactNavQuery, 'change', () => {
      const desktopHadFocus = desktopMenus.some(menu => menu.contains(document.activeElement));
      closeDesktopMenus();
      if (desktopHadFocus && compactNavQuery.matches) menuToggle?.focus({ preventScroll: true });
    });

    function mobileMenuFocusable() {
      if (!mobileMenu) return [];
      return [...mobileMenu.querySelectorAll('a[href],button:not(:disabled),summary,[tabindex]:not([tabindex="-1"])')]
        .filter(node => !node.hidden && node.getClientRects().length &&
          (!node.closest('[data-mobile-category]:not([open])') || node.matches('summary')));
    }

    function setMobileMenu(open, { restoreFocus = true } = {}) {
      if (!menuToggle || !mobileMenu || !mobileMenuBackdrop) return;
      const isOpen = Boolean(open && compactNavQuery.matches);
      const wasOpen = menuToggle.getAttribute('aria-expanded') === 'true';
      window.clearTimeout(mobileMenuCloseTimer);
      menuToggle.setAttribute('aria-expanded', String(isOpen));
      menuToggle.setAttribute('aria-label', isOpen ? closeLabel : openLabel);
      mobileMenu.setAttribute('aria-hidden', String(!isOpen));
      mobileMenu.toggleAttribute('inert', !isOpen);

      if (isOpen) {
        closeSearch();
        if (!wasOpen) {
          mobileMenuReturnFocus = document.activeElement;
          mobileMenu.querySelectorAll('[data-mobile-category]').forEach(category => {
            category.open = category.dataset.defaultOpen === 'true';
          });
        }
        mobileMenu.hidden = false;
        mobileMenuBackdrop.hidden = false;
        document.documentElement.classList.add('mobile-menu-open');
        window.requestAnimationFrame(() => {
          if (menuToggle.getAttribute('aria-expanded') !== 'true') return;
          mobileMenu.classList.add('is-open');
          mobileMenuBackdrop.classList.add('is-open');
          (mobileMenuClose || mobileMenuFocusable()[0])?.focus({ preventScroll: true });
        });
        return;
      }

      mobileMenu.classList.remove('is-open');
      mobileMenuBackdrop.classList.remove('is-open');
      document.documentElement.classList.remove('mobile-menu-open');
      mobileMenuCloseTimer = window.setTimeout(() => {
        if (menuToggle.getAttribute('aria-expanded') !== 'true') {
          mobileMenu.hidden = true;
          mobileMenuBackdrop.hidden = true;
        }
      }, 240);
      if (wasOpen && restoreFocus) {
        window.requestAnimationFrame(() => {
          (mobileMenuReturnFocus || menuToggle)?.focus({ preventScroll: true });
          mobileMenuReturnFocus = null;
        });
      }
    }

    if (menuToggle && mobileMenu && mobileMenuClose && mobileMenuBackdrop) {
      mobileMenu.setAttribute('aria-hidden', String(mobileMenu.hidden));
      mobileMenu.toggleAttribute('inert', mobileMenu.hidden);
      listen(menuToggle, 'click', () => setMobileMenu(menuToggle.getAttribute('aria-expanded') !== 'true'));
      listen(mobileMenuClose, 'click', () => setMobileMenu(false));
      listen(mobileMenuBackdrop, 'click', () => setMobileMenu(false));
      mobileMenu.querySelectorAll('a').forEach(link => listen(link, 'click', () => setMobileMenu(false, { restoreFocus: false })));
      listen(document, 'keydown', event => {
        if (menuToggle.getAttribute('aria-expanded') !== 'true') return;
        if (event.key === 'Escape') {
          event.preventDefault();
          setMobileMenu(false);
          return;
        }
        if (event.key !== 'Tab') return;
        const focusable = mobileMenuFocusable();
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      });
      const onCompactChange = () => setMobileMenu(false, { restoreFocus: false });
      if (typeof compactNavQuery.addEventListener === 'function') listen(compactNavQuery, 'change', onCompactChange);
      else {
        compactNavQuery.addListener(onCompactChange);
        cleanups.push(() => compactNavQuery.removeListener(onCompactChange));
      }
    }

    // Footer titles (data-tq-open-menu="shop|activities") open the matching header menu in place.
    const menuTargets = {
      shop: { desktop: 'desktop-shop-panel', mobile: 'mobile-sunglasses' },
      activities: { desktop: 'desktop-activities-panel', mobile: 'mobile-activities' }
    };
    root.querySelectorAll('[data-tq-open-menu]').forEach(button => {
      const target = menuTargets[button.dataset.tqOpenMenu];
      if (!target) return;
      listen(button, 'click', event => {
        event.preventDefault();
        if (compactNavQuery.matches) {
          const category = mobileMenu?.querySelector('#' + target.mobile);
          if (!menuToggle || !category) return;
          setMobileMenu(true);
          window.requestAnimationFrame(() => {
            category.open = true;
            mobileMenu.scrollTop = Math.max(0, category.offsetTop - 12);
            category.querySelector('summary')?.focus({ preventScroll: true });
          });
          return;
        }
        const menu = header.querySelector('#' + target.desktop)?.closest('details');
        if (!menu) return;
        openDesktopMenu(menu);
        menu.querySelector('summary')?.focus({ preventScroll: true });
      });
    });

    function syncHeaderHeight() {
      const headerHeight = header.getBoundingClientRect().height;
      document.documentElement.style.setProperty('--torque-v6-header-height', `${headerHeight}px`);
      let tickerHeight = 0;
      if (ticker) {
        tickerHeight = ticker.getBoundingClientRect().height;
        if (!ticker.classList.contains('is-compact')) tickerNaturalHeight = tickerHeight;
      }
      document.documentElement.style.setProperty('--torque-v6-ticker-height', `${tickerHeight}px`);
      document.documentElement.style.setProperty('--torque-v6-stickytop-height', `${headerHeight + tickerHeight}px`);
    }

    if ('ResizeObserver' in window) {
      const headerObserver = new ResizeObserver(syncHeaderHeight);
      headerObserver.observe(header);
      observers.push(headerObserver);
      if (ticker) {
        const tickerObserver = new ResizeObserver(syncHeaderHeight);
        tickerObserver.observe(ticker);
        observers.push(tickerObserver);
      }
    }
    listen(window, 'resize', syncHeaderHeight, { passive: true });
    syncHeaderHeight();

    function updateTickerCompact() {
      tickerFrame = 0;
      if (!ticker) return;
      const compact = tickerNaturalHeight > 0 && window.scrollY > tickerNaturalHeight - 1;
      ticker.classList.toggle('is-compact', compact);
    }
    if (ticker) {
      listen(window, 'scroll', () => {
        if (!tickerFrame) tickerFrame = window.requestAnimationFrame(updateTickerCompact);
      }, { passive: true });
      updateTickerCompact();
    }

    root._torqueChromeDestroy = () => {
      closeDesktopMenus();
      setMobileMenu(false, { restoreFocus: false });
      cleanups.splice(0).forEach(cleanup => cleanup());
      observers.forEach(observer => observer.disconnect());
      window.clearTimeout(mobileMenuCloseTimer);
      if (tickerFrame) window.cancelAnimationFrame(tickerFrame);
      if (mobileMenu) mobileMenu.hidden = true;
      if (mobileMenuBackdrop) mobileMenuBackdrop.hidden = true;
      document.documentElement.classList.remove('mobile-menu-open');
      delete root._torqueChromeDestroy;
      delete root.dataset.torqueChromeInitialized;
    };
  }

  function boot(scope = document) {
    const roots = [];
    if (scope.matches?.(ROOT_SELECTOR)) roots.push(scope);
    roots.push(...(scope.querySelectorAll?.(ROOT_SELECTOR) || []));
    [...new Set(roots)].forEach(init);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot(), { once: true });
  else boot();

  if (document.documentElement.dataset.torqueChromeLifecycle !== 'true') {
    document.documentElement.dataset.torqueChromeLifecycle = 'true';
    document.addEventListener('shopify:section:load', event => boot(event.target));
    document.addEventListener('shopify:section:unload', event => {
      const root = event.target.matches?.(ROOT_SELECTOR) ? event.target : event.target.querySelector?.(ROOT_SELECTOR);
      root?._torqueChromeDestroy?.();
    });
  }
})();
